/**
 * Funnel Embed Code Tab (MA-UI-023)
 * 
 * Provides embed script for installing the funnel on landing pages.
 * 
 * MA-TENANT-001: Uses embed_key for tenant-safe public resolution.
 * The embed_key is the canonical identifier - slug-based embeds are deprecated.
 */

import React, { useState } from 'react';
import { Copy, Check, Code2, ExternalLink, AlertTriangle, RefreshCw, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export default function FunnelEmbedTab({ funnelId, funnelSlug, funnelName, embedKey, onRotateKey }) {
  const [copied, setCopied] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  
  const baseUrl = window.location.origin;
  const apiUrl = window.location.origin || baseUrl;
  
  // Check if this funnel has an embed_key (new funnels) or needs migration
  const hasEmbedKey = !!embedKey;
  
  // Canonical embed URL using embed_key (preferred)
  const embedUrl = hasEmbedKey 
    ? `${baseUrl}/funnel?embed_key=${embedKey}`
    : `${baseUrl}/funnel?funnel=${funnelSlug}`; // Fallback for legacy funnels
  
  // Iframe embed with auto-scroll support using embed_key
  const iframeEmbed = hasEmbedKey ? `<!-- MissionAssist Funnel Embed -->
<script>
// Auto-scroll and resize handler for iframe
window.addEventListener('message', function(e) {
  if (!e.data || !e.data.type) return;
  var iframe = document.querySelector('iframe[data-embed-key="${embedKey}"]');
  if (!iframe) return;
  
  // Auto-scroll when funnel expands
  if (e.data.type === 'MISSIONASSIST_SCROLL_INTO_VIEW') {
    iframe.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  // Auto-resize iframe height
  if (e.data.type === 'FUNNEL_RESIZE' && e.data.payload && e.data.payload.height) {
    iframe.style.height = e.data.payload.height + 'px';
  }
});
</script>
<iframe 
  src="${baseUrl}/funnel?embed_key=${embedKey}" 
  data-embed-key="${embedKey}"
  width="100%" 
  height="500" 
  frameborder="0"
  style="border: none; max-width: 100%;"
  allow="microphone; camera"
  title="${funnelName}"
></iframe>` : `<!-- MissionAssist Funnel Embed (LEGACY - Migrate to embed_key) -->
<script>
window.addEventListener('message', function(e) {
  if (!e.data || !e.data.type) return;
  var iframe = document.querySelector('iframe[src*="${funnelSlug}"]');
  if (!iframe) return;
  if (e.data.type === 'MISSIONASSIST_SCROLL_INTO_VIEW') {
    iframe.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  if (e.data.type === 'FUNNEL_RESIZE' && e.data.payload && e.data.payload.height) {
    iframe.style.height = e.data.payload.height + 'px';
  }
});
</script>
<iframe 
  src="${baseUrl}/funnel?funnel=${funnelSlug}" 
  width="100%" 
  height="500" 
  frameborder="0"
  style="border: none; max-width: 100%;"
  allow="microphone; camera"
  title="${funnelName}"
></iframe>`;

  // JavaScript embed using embed_key (MA-EMBED-001: canonical pattern)
  const embedScript = hasEmbedKey ? `<!-- MissionAssist Funnel Embed -->
<div id="missionassist-funnel" data-embed-key="${embedKey}"></div>
<script src="${baseUrl}/embed/v1/embed.js"></script>` : `<!-- MissionAssist Funnel Embed (LEGACY - Migrate to embed_key) -->
<div id="missionassist-funnel" 
     data-tenant-id="${funnelId?.split('_')[0] || 'YOUR_TENANT_ID'}"
     data-funnel="${funnelSlug}">
</div>
<script src="${baseUrl}/embed/v1/embed.js"></script>`;

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: "Copied!",
        description: `${type} copied to clipboard`
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Please select and copy manually",
        variant: "destructive"
      });
    }
  };

  const handlePreview = () => {
    window.open(embedUrl, '_blank');
  };

  const handleRotateKey = async () => {
    if (!onRotateKey) return;
    
    setIsRotating(true);
    try {
      await onRotateKey();
      toast({
        title: "Embed Key Rotated",
        description: "A new embed key has been generated. Update your embeds with the new code."
      });
    } catch (err) {
      toast({
        title: "Rotation Failed",
        description: err.message || "Failed to rotate embed key",
        variant: "destructive"
      });
    } finally {
      setIsRotating(false);
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="funnel-embed-tab">
      <div>
        <h2 className="text-xl font-bold text-slate-100">Embed Code</h2>
        <p className="text-slate-200/60 text-sm mt-1">
          Install this funnel on your landing page
        </p>
      </div>

      {/* Migration Warning for Legacy Funnels */}
      {!hasEmbedKey && (
        <Alert className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <AlertTitle className="text-amber-200">Migration Required</AlertTitle>
          <AlertDescription className="text-amber-200/70">
            This funnel uses a legacy slug-based embed. For better security and to prevent 
            cross-tenant conflicts, please generate a new embed key. Go to the funnel 
            settings and run the migration, or contact support.
          </AlertDescription>
        </Alert>
      )}

      {/* Embed Key Info (for funnels with embed_key) */}
      {hasEmbedKey && (
        <Card className="glass border-emerald-500/20 bg-emerald-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-slate-100 text-base">
              <Shield className="w-4 h-4 text-emerald-400" />
              Secure Embed Key
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <code className="flex-1 bg-black/30 text-emerald-400 px-4 py-2 rounded-lg text-sm font-mono overflow-x-auto ring-1 ring-white/10">
                {embedKey}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(embedKey, 'Embed Key')}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              {onRotateKey && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRotateKey}
                  disabled={isRotating}
                  className="text-amber-400 hover:text-amber-300"
                  title="Rotate embed key (will invalidate existing embeds)"
                >
                  <RefreshCw className={`h-4 w-4 ${isRotating ? 'animate-spin' : ''}`} />
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-400">
              This unique key identifies your funnel securely. Rotating it will invalidate all existing embeds.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Direct Link */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <ExternalLink className="w-5 h-5 text-indigo-400" />
            Direct Link
          </CardTitle>
          <CardDescription className="text-slate-200/60">
            Share this link directly with your audience
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <code className="flex-1 bg-black/30 text-emerald-400 px-4 py-3 rounded-lg text-sm font-mono overflow-x-auto ring-1 ring-white/10">
              {embedUrl}
            </code>
            <Button
              variant="outline"
              onClick={() => handleCopy(embedUrl, 'Link')}
              className="shrink-0"
            >
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Copy
            </Button>
            <Button
              variant="outline"
              onClick={handlePreview}
              className="shrink-0"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Preview
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Iframe Embed */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Code2 className="w-5 h-5 text-indigo-400" />
            Iframe Embed (Recommended)
          </CardTitle>
          <CardDescription className="text-slate-200/60">
            Copy and paste this code into your landing page HTML
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <pre className="bg-black/30 text-emerald-400 px-4 py-4 rounded-lg text-sm font-mono overflow-x-auto ring-1 ring-white/10 whitespace-pre-wrap">
              {iframeEmbed}
            </pre>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCopy(iframeEmbed, 'Iframe embed code')}
              className="absolute top-3 right-3"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <div className="text-sm text-slate-200/60 space-y-1">
            <p><strong>Features included:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Auto-scroll:</strong> Page scrolls to show the funnel when user responds</li>
              <li><strong>Auto-resize:</strong> Iframe height adjusts as the chat expands/contracts</li>
              <li><strong>Compact start:</strong> Funnel starts small and expands after first interaction</li>
              {hasEmbedKey && <li><strong>Tenant-safe:</strong> Uses unique embed key for secure resolution</li>}
            </ul>
            <p className="mt-3"><strong>Installation:</strong></p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Copy the entire code block above (includes script + iframe)</li>
              <li>Paste it into your landing page HTML where you want the funnel to appear</li>
              <li>The funnel will automatically handle scrolling and resizing</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* JavaScript Embed */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Code2 className="w-5 h-5 text-indigo-400" />
            JavaScript Embed (Advanced)
          </CardTitle>
          <CardDescription className="text-slate-200/60">
            For advanced integrations with more control
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <pre className="bg-black/30 text-emerald-400 px-4 py-4 rounded-lg text-sm font-mono overflow-x-auto ring-1 ring-white/10 whitespace-pre-wrap">
              {embedScript}
            </pre>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCopy(embedScript, 'JavaScript embed code')}
              className="absolute top-3 right-3"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <div className="text-sm text-slate-200/60 space-y-1">
            <p><strong>Note:</strong> The JavaScript embed provides more customization options but requires the embed script to be available on your domain.</p>
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="glass border-indigo-500/20 bg-indigo-500/5">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5">
              <Code2 className="w-5 h-5 text-indigo-400" />
            </div>
            <div className="text-sm">
              <p className="text-indigo-200 font-medium mb-1">Need help with installation?</p>
              <p className="text-indigo-200/70">
                The iframe method works on most website builders including WordPress, Squarespace, Wix, and custom HTML pages. 
                For single-page applications, consider using the direct link or JavaScript embed.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
